import React, { useState } from 'react';
import { Mail, CheckCircle2, AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import { publicApiClient } from '../../apiClient';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    departmentName: '',
    contactName: '',
    email: '',
    phoneNumber: '',
    inquiryType: '',
    message: '',
    agreeToPrivacy: false,
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [ticketNo, setTicketNo] = useState('');

  const inquiryTypes = [
    'サービスについて',
    '契約内容・利用料金',
    '導入相談',
    '操作方法',
    '各種手続き（プラン変更・解約・ご登録内容の変更）',
    '不具合報告',
    '障害・エラートラブル',
    'ご要望',
    'その他',
  ];

  const validate = () => {
    const newErrors = {};

    if (formData.companyName && formData.companyName.length > 100) {
      newErrors.companyName = '企業名は100文字以内で入力してください';
    }

    if (!formData.contactName.trim()) {
      newErrors.contactName = '氏名を入力してください';
    } else if (formData.contactName.length > 100) {
      newErrors.contactName = '氏名は100文字以内で入力してください';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else {
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = '正しいメールアドレスの形式で入力してください';
      }
    }

    if (!formData.inquiryType) {
      newErrors.inquiryType = 'お問い合わせ種別を選択してください';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'お問い合わせ内容を入力してください';
    } else if (formData.message.length > 5000) {
      newErrors.message = 'お問い合わせ内容は5000文字以内で入力してください';
    }

    if (!formData.agreeToPrivacy) {
      newErrors.agreeToPrivacy = '個人情報保護方針への同意が必要です';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitContactForm = async (data) => {
    const payload = {
      company: data.companyName,
      name: data.contactName,
      email: data.email,
      category: data.inquiryType,
      message: data.message,
    };

    const response = await publicApiClient.post('/v1/lp-contact/createContactTicket', payload);
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || '送信に失敗しました。');
    }

    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    try {
      const result = await submitContactForm(formData);
      setTicketNo(result.ticketNo || '');
      setStatus('success');
    } catch (error) {
      console.error('Contact form submission error:', error);
      setStatus('error');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  if (status === 'success') {
    return (
      <section className="py-20 bg-white px-4" id="contact">
        <div className="max-w-[1000px] mx-auto bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-sm text-center">
          <div className="flex justify-center mb-6">
            <CheckCircle2 className="w-16 h-16 text-green-500" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
            お問い合わせありがとうございます。
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            内容を受け付けました。
          </p>

          {ticketNo && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 my-6 max-w-md mx-auto">
              <p className="text-sm text-gray-600 mb-1">チケット番号</p>
              <p className="text-2xl md:text-3xl font-mono font-bold text-blue-600 tracking-wider">
                {ticketNo}
              </p>
            </div>
          )}

          <p className="text-gray-600 text-sm mb-8 leading-relaxed">
            確認用の自動返信メールをお送りいたしました。<br />
            回答まで今しばらくお待ちください。
          </p>
          <button
            onClick={() => {
              setFormData({
                companyName: '',
                departmentName: '',
                contactName: '',
                email: '',
                phoneNumber: '',
                inquiryType: '',
                message: '',
                agreeToPrivacy: false,
              });
              setTicketNo('');
              setStatus('idle');
            }}
            className="bg-blue-600 text-white font-bold py-3 px-12 rounded-full hover:bg-blue-700 transition-colors shadow-lg"
          >
            閉じる
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white px-4" id="contact">
      <div className="max-w-[1000px] mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-4">お問い合わせ</h2>
          <p className="text-gray-600">
            Billwaveに関するご質問や導入のご相談など、お気軽にお問い合わせください。
          </p>
        </div>

        {/* 受付案内表示 */}
        <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-xl p-6 mb-8 text-sm leading-relaxed max-w-2xl mx-auto">
          <p className="font-bold mb-1">【お問い合わせの受付について】</p>
          <p>お問い合わせの受付は24時間365日行っております。</p>
          <p className="mt-2">
            回答は平日（土日祝日及び弊社指定休日を除く）<br />
            <span className="font-semibold">9:00〜17:00</span> となります。
          </p>
          <p className="mt-2 text-xs text-blue-800">
            ※夜間・休日に受領したお問い合わせは翌営業日以降の対応となりますのでご了承ください。
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10 shadow-sm">
          <form onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-semibold text-gray-700 mb-1">
                    企業名 <span className="text-gray-400 font-normal">(任意 / 100文字以内)</span>
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    maxLength={100}
                    placeholder="株式会社○○"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all ${
                      errors.companyName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.companyName && (
                    <p className="mt-1 text-sm text-red-500" role="alert">{errors.companyName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="contactName" className="block text-sm font-semibold text-gray-700 mb-1">
                    氏名 <span className="text-red-500" aria-hidden="true">*</span> <span className="text-gray-400 font-normal">(100文字以内)</span>
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    required
                    aria-required="true"
                    maxLength={100}
                    placeholder="山田 太郎"
                    value={formData.contactName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all ${
                      errors.contactName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.contactName && (
                    <p className="mt-1 text-sm text-red-500" role="alert">{errors.contactName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1">
                    メールアドレス <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    aria-required="true"
                    placeholder="example@company.co.jp"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500" role="alert">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="inquiryType" className="block text-sm font-semibold text-gray-700 mb-1">
                    お問い合わせ種別 <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    required
                    aria-required="true"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all appearance-none bg-white ${
                      errors.inquiryType ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">選択してください</option>
                    {inquiryTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.inquiryType && (
                    <p className="mt-1 text-sm text-red-500" role="alert">{errors.inquiryType}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-1">
                    お問い合わせ内容 <span className="text-red-500" aria-hidden="true">*</span> <span className="text-gray-400 font-normal">(5000文字以内)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    aria-required="true"
                    maxLength={5000}
                    rows="5"
                    placeholder="具体的内容を入力してください。"
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-y ${
                      errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                  ></textarea>
                  <div className="flex justify-between items-center mt-1">
                    {errors.message ? (
                      <p className="text-sm text-red-500" role="alert">{errors.message}</p>
                    ) : (
                      <span />
                    )}
                    <span className="text-xs text-gray-400">
                      {formData.message.length} / 5000文字
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="mt-10 space-y-8">
              {/* Privacy Policy Agreement */}
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="agreeToPrivacy"
                    name="agreeToPrivacy"
                    checked={formData.agreeToPrivacy}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <label htmlFor="agreeToPrivacy" className="text-sm text-gray-700 cursor-pointer select-none">
                    個人情報保護方針に同意する
                  </label>
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-0.5 ml-2 font-medium"
                  >
                    個人情報保護方針 <ExternalLink size={14} />
                  </a>
                </div>
                {errors.agreeToPrivacy && (
                  <p className="mt-1 text-sm text-red-500" role="alert">{errors.agreeToPrivacy}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="text-center" aria-live="polite">
                {status === 'error' && (
                  <div className="mb-4 flex items-center justify-center gap-2 text-red-600 font-bold">
                    <AlertCircle size={20} />
                    <span>送信できませんでした。時間をおいて再度お試しください。</span>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={status === 'submitting' || !formData.agreeToPrivacy}
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-4 px-16 rounded-full hover:bg-blue-700 transition-all shadow-lg transform hover:scale-105 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 className="animate-spin w-5 h-5" />
                      <span>送信中...</span>
                    </>
                  ) : (
                    '送信する'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
